import Analytics from '@opencare/analytics';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'opencare-crown';

import { useAnalyticsService } from 'services/AnalyticsService';
import { useRedirectionService } from 'services/RedirectionService';

import {
  FlexCol,
  FlexRow,
  P,
} from '../../../../common/styles/oc-styled-components';
import { colors } from '../../../../common/styles/style-constants';
import { sendPartnerLead } from '../../../../connect/actions';
import PhoneSvg from '../../../common/components/svgs/PhoneSvg';
import {
  checkEmail,
  checkNumberString,
} from '../../../common/helpers/form-field-helpers';
import { useFunnelStorage } from '../../../funnel/storage/Storage';
import { CONCIERGE_KEY, generateStepAnalytics } from '../../utils';
import CompletedFormBadge from '../common/Form/CompletedFormBadge';
import FormInput from '../common/Form/FormInput';

import {
  ConciergeForm,
  ConciergeFormWrapper,
  ContactWrapper,
  FormButton,
  FormHeader,
  HorizontalBreak,
  Label,
  Title,
} from './styled-component';

const analytics = new Analytics();

const Contact = ({ position }) => {
  const dispatch = useDispatch();
  const storage = useFunnelStorage();
  const [conciergeForm] = Form.useForm();

  const analyticsService = useAnalyticsService();
  const redirectionService = useRedirectionService();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [showCompletedBadge, setShowCompletedBadge] = useState(false);

  const location = storage.get(CONCIERGE_KEY.LOCATION);

  const partnerPhoneNumber = '+18552114427';
  const partnerPhoneNumberFormatted = '(855) 211 4427';

  useEffect(() => {
    if (storage.loading) {
      return;
    }

    analytics.trackPageView({
      pageType: analytics.pageTypes.CONCIERGE,
      pageName: 'concierge_contact',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });

    const patientInformation = storage.get(CONCIERGE_KEY.CONTACT);
    if (!patientInformation) {
      return;
    }

    setFirstName(patientInformation.firstName);
    setLastName(patientInformation.lastName);
    setEmail(patientInformation.email);
    setPhone(patientInformation.phoneNumber.replace('+1', ''));
    setShowCompletedBadge(true);
  }, [storage.loading]);

  const handleFinish = () => {
    if (ValidateFields()) {
      const patientInformation = {
        firstName,
        lastName,
        email,
        phoneNumber: `+1${phone}`,
      };

      analytics.trackForm({
        pageType: analytics.pageTypes.CONCIERGE,
        pageName: 'concierge_contact',
        ...generateStepAnalytics(position, storage, patientInformation),
      });

      dispatch(
        sendPartnerLead(
          storage.get(CONCIERGE_KEY.PARTNER).id,
          patientInformation,
          location.address,
          {
            reason: storage.get(CONCIERGE_KEY.REASON).reasonPartnerId,
          },
        ),
      );
      storage.save(CONCIERGE_KEY.CONTACT, patientInformation, false);
      setShowCompletedBadge(true);
    }
  };

  const ValidateFields = () => {
    const error = {};
    if (!firstName || firstName === '' || firstName === undefined) {
      error.firstName = 'First name is required';
    }
    if (!lastName || lastName === '' || lastName === undefined) {
      error.lastName = 'Last name is required';
    }
    if (!email) {
      error.email = 'Email is required';
    } else {
      checkEmail(null, email, (message) => {
        if (message) {
          error.email = message;
        }
      });
    }
    if (!phone) {
      error.phone = 'Phone is required';
    } else {
      checkNumberString(null, phone, (message) => {
        if (message) error.phone = message;
      });
    }
    setErrors(error);
    return Object.keys(error).length === 0 && error.constructor === Object;
  };

  const callPartner = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.CONCIERGE,
      pageName: 'concierge_contact',
      ...generateStepAnalytics(position, storage),
    });

    redirectionService.redirect({
      url: `tel:${partnerPhoneNumber}`,
      reason: 'concierge-contact-call',
    });
  };

  return (
    <ContactWrapper>
      <FlexCol align="center">
        <Title>
          <span role="img" aria-label="search">
            🔎
          </span>{' '}
          Let’s get you the perfect dentist in {location.address.locality}.
        </Title>

        <ConciergeFormWrapper>
          <FormHeader>Opencare Concierge</FormHeader>

          <ConciergeForm form={conciergeForm} onFinish={handleFinish}>
            {showCompletedBadge && <CompletedFormBadge firstName={firstName} />}

            <Label>
              Please enter your contact information below and an Opencare
              Concierge team member will call you within the next 5 minutes.
            </Label>

            <FlexRow justify="space-between" mobileDirection="column">
              <FormInput
                field="First Name"
                label="firstName"
                initialValue={firstName}
                required
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={errors.firstName}
                disabled={showCompletedBadge}
              />

              <FormInput
                field="Last Name"
                label="lastName"
                initialValue={lastName}
                message="Required, Please enter a valid Last Name"
                required
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={errors.lastName}
                disabled={showCompletedBadge}
              />
            </FlexRow>

            <FlexRow justify="space-between" mobileDirection="column">
              <FormInput
                field="Phone Number"
                label="phone"
                initialValue={phone}
                message="Required, Please enter a valid Phone Number"
                required
                type="tel"
                validator={checkNumberString}
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={errors.phone}
                disabled={showCompletedBadge}
              />

              <FormInput
                field="Email"
                label="email"
                initialValue={email}
                required
                type="email"
                validator={checkEmail}
                placeholder="Email"
                value={email}
                setValue={setEmail}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                disabled={showCompletedBadge}
              />
            </FlexRow>

            <FormButton
              type="submit"
              width="100%"
              data-testid="continue"
              disabled={showCompletedBadge}
            >
              Find me a dentist
            </FormButton>

            <HorizontalBreak>or call us now</HorizontalBreak>

            <FormButton
              type="button"
              width="100%"
              data-testid="callus"
              onClick={callPartner}
            >
              <PhoneSvg />
              {partnerPhoneNumberFormatted}
            </FormButton>

            <P fontSize="12px" color={colors.sage7} margin="10px auto 0">
              Opencare Concierge is powered by 1-800 DENTIST
            </P>
          </ConciergeForm>
        </ConciergeFormWrapper>
      </FlexCol>
    </ContactWrapper>
  );
};

Contact.propTypes = {
  position: PropTypes.string,
};

export default Contact;
